import { useState, useEffect, useRef, useCallback } from "react";

export const useColumnResizing = ({
  initialColumnSizing = {}, // Provide a default empty object
  onColumnSizingChange,
  onContextColumnSizingChange,
  tableContainerRef,
}) => {
  // Internal state for the hook, initialized by prop
  // This helps if the hook needs to manage its own version during drag,
  // but the final source of truth will be updated via callbacks.
  const [currentColumnSizing, setCurrentColumnSizing] =
    useState(initialColumnSizing);

  // Refs for drag operation
  const isResizingRef = useRef(false);
  const resizingColumnIdRef = useRef(null);
  const initialClientXRef = useRef(0);
  const initialColumnWidthRef = useRef(0);

  // Refs for DOM elements created by the hook
  const resizeLineRef = useRef(null);
  const resizeGhostRef = useRef(null);
  const styleElementRef = useRef(null); // To manage the <style> tag

  // Ref for the table element (passed or found via tableContainerRef)
  const tableRef = useRef(null);
  const rafIdRef = useRef(null); // For requestAnimationFrame

  // Effect to setup and cleanup resize DOM elements and global listeners
  useEffect(() => {
    if (!resizeLineRef.current) {
      const line = document.createElement("div");
      line.className = "column-resize-line";
      line.style.position = "absolute";
      line.style.width = "2px";
      line.style.backgroundColor = "#3b82f6";
      line.style.zIndex = "1000";
      line.style.top = "0";
      line.style.bottom = "0";
      line.style.display = "none";
      document.body.appendChild(line);
      resizeLineRef.current = line;
    }
    if (!resizeGhostRef.current) {
      const ghost = document.createElement("div");
      ghost.className = "column-resize-ghost";
      ghost.style.position = "absolute";
      ghost.style.backgroundColor = "rgba(59, 130, 246, 0.1)";
      ghost.style.borderRight = "2px solid #3b82f6";
      ghost.style.zIndex = "990";
      ghost.style.display = "none";
      document.body.appendChild(ghost);
      resizeGhostRef.current = ghost;
    }

    if (!styleElementRef.current) {
      const styleEl = document.createElement("style");
      styleEl.textContent = `
        .table-container:not(.resizing-active) table th,
        .table-container:not(.resizing-active) table td {
          /* transition: width 0.15s ease-out; */
        }
        .column-resizer {
          position: absolute;
          right: 0;
          top: 0;
          bottom: 0;
          width: 5px;
          cursor: col-resize;
          z-index: 10; /* Ensure it's above cell content but below other UI */
        }
        .column-resizer:hover,
        .column-resizer.active {
          background-color: #3b82f6;
          opacity: 0.5;
        }
        body.resizing-columns, body.resizing-columns * {
          cursor: col-resize !important;
        }

        /* Filter button styles - ADD THESE BACK IN */
        .filter-button {
          position: absolute;
          bottom: 0; /* Adjust as needed, e.g., bottom: 2px; */
          left: 50%;
          transform: translate(-50%, -1px); /* Adjust Y transform if bottom changes */
          z-index: 20; /* Ensure it's above resizer if they overlap */
          padding: 2px;
          border-radius: 50%; /* For a circular button */
          background-color: transparent; /* Or a default background */
          transition: background-color 0.2s;
          /* Ensure it's clickable */
          cursor: pointer; 
        }
        
        .filter-button:hover {
          background-color: rgba(243, 244, 246, 0.8); /* gray-100 with opacity */
        }
        /* END OF ADDED STYLES */
      `;
      document.head.appendChild(styleEl);
      styleElementRef.current = styleEl;
    }

    // Cleanup on unmount
    return () => {
      if (resizeLineRef.current) {
        resizeLineRef.current.remove();
        resizeLineRef.current = null;
      }
      if (resizeGhostRef.current) {
        resizeGhostRef.current.remove();
        resizeGhostRef.current = null;
      }
      if (styleElementRef.current) {
        styleElementRef.current.remove();
        styleElementRef.current = null;
      }
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      // Ensure event listeners are removed
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      if (tableContainerRef.current) {
        tableContainerRef.current.classList.remove("resizing-active");
      }
      document.body.classList.remove("resizing-columns");
    };
  }, [tableContainerRef]); // tableContainerRef should be stable

  const handleMouseMove = useCallback((e) => {
    if (!isResizingRef.current || !resizingColumnIdRef.current) return;

    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
    }

    rafIdRef.current = requestAnimationFrame(() => {
      const deltaX = e.clientX - initialClientXRef.current;
      const newWidth = Math.max(50, initialColumnWidthRef.current + deltaX); // Min width 50px

      if (resizeGhostRef.current) {
        resizeGhostRef.current.style.width = `${newWidth}px`;
      }
      if (resizeLineRef.current) {
        resizeLineRef.current.style.left = `${e.clientX}px`;
      }

      // Direct DOM manipulation for smoothness
      if (tableRef.current && resizingColumnIdRef.current) {
        const columnCells = tableRef.current.querySelectorAll(
          `th[data-column-id="${resizingColumnIdRef.current}"], td[data-column-index="${resizingColumnIdRef.current}"]`
        );
        columnCells.forEach((cell) => {
          cell.style.width = `${newWidth}px`;
        });
      }
    });
  }, []); // No dependencies needed if all refs are stable

  const handleMouseUp = useCallback(
    (e) => {
      if (!isResizingRef.current || !resizingColumnIdRef.current) return;

      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      if (resizeGhostRef.current) resizeGhostRef.current.style.display = "none";
      if (resizeLineRef.current) resizeLineRef.current.style.display = "none";

      if (tableContainerRef.current) {
        tableContainerRef.current.classList.remove("resizing-active");
      }

      const deltaX = e.clientX - initialClientXRef.current;
      const finalWidth = Math.max(50, initialColumnWidthRef.current + deltaX);

      // Update the sizing state via callbacks
      // The hook itself doesn't hold the "final" state, it informs the parent
      const newSizingUpdate = { [resizingColumnIdRef.current]: finalWidth };

      if (typeof onColumnSizingChange === "function") {
        onColumnSizingChange((prev) => ({ ...prev, ...newSizingUpdate }));
      }
      if (typeof onContextColumnSizingChange === "function") {
        onContextColumnSizingChange((prev) => ({
          ...prev,
          ...newSizingUpdate,
        }));
      }
      // Update internal state if needed, or rely on prop changes
      setCurrentColumnSizing((prev) => ({ ...prev, ...newSizingUpdate }));

      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.classList.remove("resizing-columns");

      isResizingRef.current = false;
      resizingColumnIdRef.current = null;
    },
    [onColumnSizingChange, onContextColumnSizingChange, tableContainerRef]
  ); // Add callbacks to dependency array

  const handleResizeStart = useCallback(
    (e, columnId) => {
      e.preventDefault();
      e.stopPropagation();

      resizingColumnIdRef.current = columnId;
      initialClientXRef.current = e.clientX;

      // Get actual column width from DOM if not in currentColumnSizing
      let actualWidth = currentColumnSizing[columnId];
      if (!actualWidth) {
        const columnElement = tableContainerRef.current?.querySelector(
          `th[data-column-id="${columnId}"]`
        );
        actualWidth = columnElement ? columnElement.offsetWidth : 150; // fallback to 150 if element not found
      }

      initialColumnWidthRef.current = actualWidth;
      isResizingRef.current = true;
      console.log(tableContainerRef.current);
      if (tableContainerRef.current) {
        tableContainerRef.current.classList.add("resizing-active");
        // Find the actual table element within the container
        const currentTableElement =
          tableContainerRef.current.querySelector("table");
        if (currentTableElement) {
          tableRef.current = currentTableElement;
        }
      }
      document.body.classList.add("resizing-columns");

      if (tableRef.current) {
        const tableRect = tableRef.current.getBoundingClientRect();
        const columnElement = tableRef.current.querySelector(
          `th[data-column-id="${columnId}"]`
        );

        if (columnElement && resizeGhostRef.current) {
          const rect = columnElement.getBoundingClientRect();
          resizeGhostRef.current.style.top = `${rect.top + window.scrollY}px`; // Account for scroll
          resizeGhostRef.current.style.left = `${rect.left + window.scrollX}px`; // Account for scroll
          resizeGhostRef.current.style.width = `${rect.width}px`;
          resizeGhostRef.current.style.height = `${tableRef.current.offsetHeight}px`;
          resizeGhostRef.current.style.display = "block";
        }
        if (resizeLineRef.current) {
          resizeLineRef.current.style.top = `${
            tableRect.top + window.scrollY
          }px`; // Account for scroll
          resizeLineRef.current.style.height = `${tableRef.current.offsetHeight}px`;
          resizeLineRef.current.style.left = `${e.clientX}px`; // Line follows cursor
          resizeLineRef.current.style.display = "block";
        }
      }

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [currentColumnSizing, tableContainerRef, handleMouseMove, handleMouseUp]
  ); // Add currentColumnSizing and handlers

  // Effect to sync the hook's internal state if the initialColumnSizing prop changes
  // This is important if BaseTable's columnSizing is updated from context or other sources
  useEffect(() => {
    setCurrentColumnSizing(initialColumnSizing);
  }, [initialColumnSizing]);

  return {
    handleResizeStart,
  };
};
